import {
  deltaMilliseconds,
  localeMap,
} from "../../components/formatted/FormattedDateAndTime";
import { sortObjectsInArrayByKeyName } from "../../components/utils/Object";
import { mergeMaterialFluxes } from "../characterization-2/core/utils/mergeMaterialFluxes";
import { buildMassSeries } from "../characterization-2/core/utils/metrics";
import {
  aggregateMaterialDelivery,
  apiWeightToTonnes,
} from "../characterization-2/core/utils/utils";

const computePercentageChange = (current, previous) => {
  if (!previous || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
};

export const resolveNumberLocale = (language) => {
  if (!language) return "fr-FR";
  if (language === "se") return "sv-SE";
  return localeMap[language] || "fr-FR";
};

export const formatCount = (value, locale) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Math.round(Number(value)).toLocaleString(locale);
};

export const errorsPerRotation = (errors, rotations) => {
  const errorCount = Number(errors);
  const rotationCount = Number(rotations);
  if (!Number.isFinite(errorCount) || !Number.isFinite(rotationCount)) {
    return null;
  }
  if (rotationCount <= 0) return null;
  return errorCount / rotationCount;
};

export const formatRate = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

export const formatVariation = (current, previous) => {
  const variation = computePercentageChange(current, previous);
  if (variation == null) return "—";
  return `${variation > 0 ? "+" : ""}${variation}%`;
};

export const getPreviousPeriod = (periodStart, periodStop) => {
  const start = new Date(periodStart);
  const stop = new Date(periodStop);
  const delta = deltaMilliseconds(start, stop);
  return {
    periodStart: new Date(start.getTime() - delta).toISOString(),
    periodStop: start.toISOString(),
  };
};

const ONE_MONTH_MS = 31 * 24 * 60 * 60 * 1000;

const collectMonthlyTimes = (dataResult) => {
  const times = [];
  Object.values(dataResult || {}).forEach((flux) => {
    (flux?.monthlyDistribution || []).forEach((item) => {
      const time = new Date(item?.time).getTime();
      if (Number.isFinite(time)) times.push(time);
    });
  });
  return times;
};

export const toMonthKey = (time) => {
  const date = new Date(time);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  return year && month ? `${year}-${month}` : null;
};

export const formatChartMonthTitle = (time, locale) => {
  if (!time) return "";
  const iso = /^\d{4}-\d{2}$/.test(String(time))
    ? `${time}-01T12:00:00.000Z`
    : time;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const label = date.toLocaleDateString(locale || "fr-FR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const sumMonthlyField = (items, field) =>
  (items || []).reduce((sum, item) => sum + (Number(item?.[field]) || 0), 0);

const buildRotationCountByFluxAndMonth = (collectionDataResult) => {
  const byFlux = {};
  Object.entries(collectionDataResult || {}).forEach(([flux, data]) => {
    const months = {};
    (data?.monthlyDistribution || []).forEach((item) => {
      const key = toMonthKey(item.time);
      if (!key) return;
      months[key] = (months[key] || 0) + (Number(item.countCollection) || 0);
    });
    byFlux[flux] = months;
  });
  return byFlux;
};

export const buildSortingErrorRateByFluxSeries = (
  sortingDataResult,
  collectionDataResult,
) => {
  const rotationsByFluxMonth =
    buildRotationCountByFluxAndMonth(collectionDataResult);
  const monthKeys = new Set();

  Object.values(sortingDataResult || {}).forEach((fluxData) => {
    (fluxData?.monthlyDistribution || []).forEach((item) => {
      const key = toMonthKey(item.time);
      if (key) monthKeys.add(key);
    });
  });

  const labels = [...monthKeys].sort();
  const series = Object.keys(sortingDataResult || {}).map((flux) => {
    const errorsByMonth = {};
    (sortingDataResult[flux]?.monthlyDistribution || []).forEach((item) => {
      const key = toMonthKey(item.time);
      if (!key) return;
      errorsByMonth[key] =
        (errorsByMonth[key] || 0) + (Number(item.countAnomalies) || 0);
    });

    return {
      flux,
      data: labels.map((key) => {
        const rate = errorsPerRotation(
          errorsByMonth[key] || 0,
          rotationsByFluxMonth[flux]?.[key] || 0,
        );
        return {
          x: key,
          y: rate == null ? 0 : Math.round(rate * 10) / 10,
          z: flux,
        };
      }),
    };
  });

  return { series, labels };
};

export const buildSortingErrorRateComposition = (
  sortingDataResult,
  collectionDataResult,
) => {
  const rotationTotals = {};
  Object.entries(collectionDataResult || {}).forEach(([flux, data]) => {
    rotationTotals[flux] = sumMonthlyField(
      data?.monthlyDistribution,
      "countCollection",
    );
  });

  return Object.keys(sortingDataResult || {})
    .map((flux) => {
      const fluxData = sortingDataResult[flux] || {};
      const errors = Number.isFinite(Number(fluxData.countAnomalies))
        ? Number(fluxData.countAnomalies)
        : sumMonthlyField(fluxData.monthlyDistribution, "countAnomalies");
      const rate = errorsPerRotation(errors, rotationTotals[flux] || 0);
      return { label: flux, value: rate ?? 0 };
    })
    .filter((segment) => segment.value > 0)
    .sort((left, right) => right.value - left.value);
};

export const hasComparablePreviousSortingHistory = (
  previousDataResult,
  previousPeriodStart,
) => {
  if (!previousDataResult || Object.keys(previousDataResult).length === 0) {
    return false;
  }
  const periodStartMs = new Date(previousPeriodStart).getTime();
  if (!Number.isFinite(periodStartMs)) return false;
  const times = collectMonthlyTimes(previousDataResult);
  if (times.length === 0) return true;
  return Math.min(...times) <= periodStartMs + ONE_MONTH_MS;
};

export const readBooleanStorage = (key, fallback = true) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored === "false") return false;
    if (stored === "true") return true;
  } catch {
    return fallback;
  }
  return fallback;
};

export const writeBooleanStorage = (key, value) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
};

const toContainerId = (value) => (value == null ? "" : String(value));

const groupDashboardZones = (containers, superContainers) => {
  const list = Array.isArray(containers) ? containers : [];
  const supers = Array.isArray(superContainers) ? superContainers : [];
  const containersById = new Map(
    list.map((container) => [toContainerId(container._id), container]),
  );
  const groupedIds = new Set();
  const zones = [];

  supers.forEach((superContainer) => {
    const members = (superContainer?.containerObjectIdList || [])
      .map((id) => containersById.get(toContainerId(id)))
      .filter(Boolean);
    if (members.length === 0) return;
    members.forEach((member) => groupedIds.add(toContainerId(member._id)));
    zones.push(members[0]);
  });

  list.forEach((container) => {
    if (groupedIds.has(toContainerId(container._id))) return;
    zones.push(container);
  });

  return zones;
};

export const buildDashboardSiteZoneCounts = (containers, superContainers) => {
  const zones = groupDashboardZones(containers, superContainers);
  const byAddress = new Map();

  zones.forEach((zone) => {
    const addressKey =
      zone.addressId == null ? zone.addressName || "" : String(zone.addressId);
    if (!byAddress.has(addressKey)) {
      byAddress.set(addressKey, {
        addressName: zone.addressName,
        count: 1,
      });
      return;
    }
    byAddress.get(addressKey).count += 1;
  });

  return {
    addresses: [...byAddress.values()],
    zoneCount: zones.length,
  };
};

export const shouldHideDashboardRotations = ({ containers } = {}) => {
  const list = Array.isArray(containers) ? containers : [];
  return !list.some((container) => container.activatedFillingLevelOption);
};

/** Align with WQ / dashboard "en cours": exclude mobile (bennes) zones. */
const isMobileCharacterizationZone = (site) =>
  Boolean(site?.positioning) &&
  String(site.positioning).toLowerCase() === "mobile";

export const isLeftCharacterizationZone = (site) => {
  const pos = String(site?.positioning || "").toLowerCase();
  return pos === "gauche" || pos === "left" || pos === "l";
};

/** Same window as dashboard L/R period dedupe. */
export const CHARACTERIZATION_REPORT_DEDUP_MS = 2 * 60 * 1000;

/**
 * One query target per non-mobile zone (all supercontainer members + standalones).
 */
export const buildCharacterizationReportQueryTargets = (
  allContainers,
  superContainers,
) => {
  const containers = Array.isArray(allContainers) ? allContainers : [];
  const supers = Array.isArray(superContainers) ? superContainers : [];
  const containersById = new Map(
    containers.map((container) => [String(container._id), container]),
  );
  const groupedIds = new Set();
  const targets = [];

  supers.forEach((superContainer) => {
    const members = (superContainer.containerObjectIdList || [])
      .map((id) => containersById.get(String(id)))
      .filter(
        (container) => container && !isMobileCharacterizationZone(container),
      );
    if (members.length === 0) return;

    // Prefer left first so concurrent results stay deterministic when deduping.
    members.sort((left, right) => {
      const leftIsLeft = isLeftCharacterizationZone(left);
      const rightIsLeft = isLeftCharacterizationZone(right);
      if (leftIsLeft === rightIsLeft) return 0;
      return leftIsLeft ? -1 : 1;
    });

    members.forEach((member) => groupedIds.add(String(member._id)));
    members.forEach((member) => {
      targets.push({
        container: member,
        containerId: String(member._id),
        superContainerId:
          superContainer._id != null ? String(superContainer._id) : null,
        zoneId:
          superContainer._id != null
            ? String(superContainer._id)
            : String(member._id),
        addressName: member.addressName || superContainer.addressName || "",
      });
    });
  });

  containers.forEach((container) => {
    if (
      groupedIds.has(String(container._id)) ||
      isMobileCharacterizationZone(container)
    ) {
      return;
    }
    targets.push({
      container,
      containerId: String(container._id),
      superContainerId: null,
      zoneId: String(container._id),
      addressName: container.addressName || "",
    });
  });

  return targets;
};

/**
 * Collapse L/R (or multi-member) reports that belong to the same site event.
 * Prefers periods that already have client/weight metadata.
 */
export const dedupeCharacterizationDashboardReports = (
  reports,
  windowMs = CHARACTERIZATION_REPORT_DEDUP_MS,
) => {
  const kept = [];
  const sorted = [...(reports || [])].sort(
    (left, right) => new Date(right.start) - new Date(left.start),
  );

  sorted.forEach((report) => {
    if (!report?.start) return;
    const startMs = new Date(report.start).getTime();
    if (Number.isNaN(startMs)) return;
    const groupId = String(
      report.groupId || report.zoneId || report.containerId,
    );

    const duplicateIndex = kept.findIndex((existing) => {
      const existingGroup = String(
        existing.groupId || existing.zoneId || existing.containerId,
      );
      if (existingGroup !== groupId) return false;
      return Math.abs(new Date(existing.start).getTime() - startMs) < windowMs;
    });

    if (duplicateIndex < 0) {
      kept.push(report);
      return;
    }

    const duplicate = kept[duplicateIndex];
    const duplicateHasClient =
      Boolean(duplicate.weight) || Boolean(duplicate.client);
    const reportHasClient = Boolean(report.weight) || Boolean(report.client);
    if (!duplicateHasClient && reportHasClient) {
      kept[duplicateIndex] = report;
    }
  });

  return kept.sort(
    (left, right) => new Date(right.start) - new Date(left.start),
  );
};

/** Map API periods for one zone into dashboard report rows. */
export const mapTargetPeriodsToCharacterizationReports = (
  periods,
  { containerId, zoneId, superContainerId },
) => {
  const groupId = String(superContainerId || zoneId || containerId || "");
  return (periods || [])
    .filter((period) => period?.start && period?.stop)
    .map((period) => ({
      key: `${groupId}-${period.start}`,
      groupId,
      containerId: containerId != null ? String(containerId) : "",
      zoneId: zoneId != null ? String(zoneId) : String(containerId || ""),
      superContainerId: superContainerId || null,
      start: period.start,
      stop: period.stop,
      client: period.client,
      waste_category: period.waste_category,
      weight: period.weight,
    }));
};

const parseCharacterizationJsonField = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getCharacterizationFlowName = (wasteCategory) => {
  const parsed = parseCharacterizationJsonField(wasteCategory) || wasteCategory;
  if (parsed == null) return "";
  if (typeof parsed === "object") {
    return (
      parsed.wasteCategoryName ||
      parsed.label ||
      parsed.name ||
      ""
    ).trim();
  }
  const trimmed = String(parsed).trim();
  return trimmed === "-" ? "" : trimmed;
};

export const getCharacterizationClientLabel = (client) => {
  const parsed = parseCharacterizationJsonField(client);
  if (!parsed || typeof parsed !== "object") return "";
  return (parsed.address || parsed.name || "").trim();
};

export const mapWithConcurrency = async (items, limit, mapper) => {
  const list = Array.isArray(items) ? items : [];
  const concurrency = Math.max(1, Number(limit) || 1);
  const results = new Array(list.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < list.length) {
      const current = nextIndex;
      nextIndex += 1;
      results[current] = await mapper(list[current], current);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, list.length) }, () => worker()),
  );
  return results;
};

const accumulateMaterialRow = (item, incoming) => {
  item.count += +(incoming.count || 0);
  item.volumeObject += +(incoming.volumeObject || 0);
  item.massObject += +(incoming.massObject || 0);
  item.finesMass += +(incoming.finesMass || 0);
  item.finesVolume += +(incoming.finesVolume || 0);
  item.surface += +(incoming.surface || 0);
  return item;
};

export const prepareCharacterizationReportMaterials = (
  materialDelivery,
  mergeConfig,
) => {
  const concatenated = [
    ...(materialDelivery || [])
      .reduce((rows, material) => {
        const sizeRange = material.sizeRange || "total";
        const key = `${material.cls}_${sizeRange}`;
        const existing = rows.get(key);
        if (!existing) {
          rows.set(
            key,
            accumulateMaterialRow(
              {
                ...material,
                sizeRange,
                count: 0,
                volumeObject: 0,
                massObject: 0,
                finesMass: 0,
                finesVolume: 0,
                surface: 0,
              },
              material,
            ),
          );
          return rows;
        }
        accumulateMaterialRow(existing, material);
        return rows;
      }, new Map())
      .values(),
  ];

  const merged =
    Array.isArray(mergeConfig) && mergeConfig.length > 0
      ? mergeMaterialFluxes(concatenated, mergeConfig)
      : concatenated;

  return sortObjectsInArrayByKeyName(aggregateMaterialDelivery(merged), "cls");
};

export const formatCharacterizationRegisteredWeight = (
  weight,
  tonsLabel,
  locale,
) => {
  const tonnes = apiWeightToTonnes(weight);
  if (tonnes == null || tonnes === 0) return "—";
  return `${tonnes.toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} ${tonsLabel}`;
};

export const getCharacterizationClientQueryBounds = (start, stop) => {
  const startMs = new Date(start).getTime();
  const stopMs = new Date(stop).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(stopMs)) return null;
  return {
    start: new Date(startMs - 1).toISOString(),
    stop: new Date(stopMs + 1).toISOString(),
  };
};

export const buildWeightedMassComposition = (reports) => {
  const byKey = new Map();
  let totalWeight = 0;

  (reports || []).forEach((report) => {
    if (report?.status && report.status !== "ready") return;
    const weightKg = Number(report?.weight);
    if (!Number.isFinite(weightKg) || weightKg <= 0) return;
    const labels = report.massChart?.labels || [];
    const values = report.massChart?.values || [];
    const keys = report.massChart?.keys || [];
    if (labels.length === 0) return;
    totalWeight += weightKg;
    labels.forEach((label, index) => {
      const percent = Number(values[index]) || 0;
      const key = keys[index] || label;
      const existing = byKey.get(key);
      byKey.set(key, {
        key,
        label: existing?.label || label,
        mass: (existing?.mass || 0) + weightKg * (percent / 100),
      });
    });
  });

  if (totalWeight <= 0) {
    return { labels: [], values: [], keys: [], colors: [], totalWeight: 0 };
  }

  const entries = [...byKey.values()]
    .map((entry) => ({
      ...entry,
      value: (entry.mass / totalWeight) * 100,
    }))
    .sort((left, right) => right.value - left.value);

  return {
    labels: entries.map((entry) => entry.label),
    values: entries.map((entry) => entry.value),
    keys: entries.map((entry) => entry.key),
    colors: entries.map(() => "rgba(75,192,192,0.6)"),
    totalWeight,
  };
};

export const buildWeightedObjectComposition = (reports) => {
  const byLabel = new Map();
  let totalObjects = 0;

  (reports || []).forEach((report) => {
    if (report?.status && report.status !== "ready") return;
    const labels = report.objectChart?.labels || [];
    const values = report.objectChart?.values || [];
    const chartTotal = values.reduce(
      (sum, value) => sum + (Number(value) || 0),
      0,
    );
    const parsedTotal = Number(report.materialTotal);
    const reportTotal =
      report.materialTotal == null || report.materialTotal === ""
        ? chartTotal
        : parsedTotal;
    if (
      !Number.isFinite(reportTotal) ||
      reportTotal <= 0 ||
      chartTotal <= 0 ||
      labels.length === 0
    ) {
      return;
    }
    totalObjects += reportTotal;
    const keys = report.objectChart?.keys || [];
    labels.forEach((label, index) => {
      const count = Number(values[index]) || 0;
      const proportion = count / chartTotal;
      const key = keys[index] || label;
      const existing = byLabel.get(key);
      byLabel.set(key, {
        key,
        label: existing?.label || label,
        count: (existing?.count || 0) + reportTotal * proportion,
      });
    });
  });

  if (totalObjects <= 0) {
    return { labels: [], values: [], keys: [], colors: [], totalObjects: 0 };
  }

  const entries = [...byLabel.values()]
    .map((entry) => ({
      ...entry,
      value: (entry.count / totalObjects) * 100,
    }))
    .sort((left, right) => right.value - left.value);

  return {
    labels: entries.map((entry) => entry.label),
    values: entries.map((entry) => entry.value),
    keys: entries.map((entry) => entry.key),
    colors: entries.map(() => "rgba(75,192,192,0.6)"),
    totalObjects,
  };
};

export const groupMinorCharacterizationShares = (
  composition,
  {
    thresholdPercent = 1,
    finesKey = "fines",
    othersKey = "others",
    formatOthersLabel,
  } = {},
) => {
  const labels = composition?.labels || [];
  const values = composition?.values || [];
  const keys = composition?.keys || [];
  const entries = labels.map((label, index) => ({
    key: keys[index] || label,
    label,
    value: Number(values[index]) || 0,
  }));
  const kept = [];
  const minor = [];
  entries.forEach((entry) => {
    if (entry.key === finesKey || entry.value >= thresholdPercent) {
      kept.push(entry);
    } else {
      minor.push(entry);
    }
  });
  kept.sort((left, right) => right.value - left.value);
  if (minor.length === 0) {
    return {
      ...composition,
      labels: kept.map((entry) => entry.label),
      values: kept.map((entry) => entry.value),
      keys: kept.map((entry) => entry.key),
    };
  }
  minor.sort((left, right) => right.value - left.value);
  const othersValue = minor.reduce((sum, entry) => sum + entry.value, 0);
  const preview = minor.slice(0, 2).map((entry) => entry.label);
  const othersLabel = formatOthersLabel
    ? formatOthersLabel(preview, minor.length > 2)
    : `Others (${preview.join(", ")}${minor.length > 2 ? ", etc." : ""})`;
  const result = minor.length
    ? [...kept, { key: othersKey, label: othersLabel, value: othersValue }]
    : kept;
  result.sort((left, right) => right.value - left.value);
  return {
    ...composition,
    labels: result.map((entry) => entry.label),
    values: result.map((entry) => entry.value),
    keys: result.map((entry) => entry.key),
  };
};

export const buildCharacterizationMassChartData = ({
  materialDelivery,
  totalWasteMass,
  totalFinesMass,
  finesBelowThreshold,
  finesLabel,
}) => {
  const mass = buildMassSeries({
    materialDelivery,
    totalWasteMass,
  });
  const finesMass =
    (Number(finesBelowThreshold?.massObject) || 0) +
    (Number(totalFinesMass) || 0);
  const hasFines =
    finesMass > 0 ||
    (Number(finesBelowThreshold?.count) || 0) > 0 ||
    (Number(finesBelowThreshold?.volumeObject) || 0) > 0;
  const finesPercent =
    hasFines && totalWasteMass > 0
      ? (finesMass / totalWasteMass) * 100
      : hasFines
        ? 0
        : null;

  return {
    labels: [
      ...(mass.labels || []),
      ...(finesPercent != null ? [finesLabel] : []),
    ],
    keys: [
      ...(materialDelivery || []).map((material) => material.cls),
      ...(finesPercent != null ? ["fines"] : []),
    ],
    values: [
      ...(mass.values || []),
      ...(finesPercent != null ? [finesPercent] : []),
    ],
    colors: [
      ...(mass.colors || []),
      ...(finesPercent != null ? ["rgba(75,192,192,0.6)"] : []),
    ],
  };
};

export const buildCharacterizationObjectChartData = ({
  materialDelivery,
  finesBelowThreshold,
  totalFinesVolume,
  finesLabel,
  translateLabel,
}) => {
  const finesCount = Number(finesBelowThreshold?.count) || 0;
  const finesVolume =
    (Number(finesBelowThreshold?.volumeObject) || 0) +
    (Number(totalFinesVolume) || 0);
  const hasFines = finesVolume > 0 || finesCount > 0;

  return {
    labels: [
      ...(materialDelivery || []).map((material) =>
        translateLabel(material.cls),
      ),
      ...(hasFines ? [finesLabel] : []),
    ],
    keys: [
      ...(materialDelivery || []).map((material) => material.cls),
      ...(hasFines ? ["fines"] : []),
    ],
    values: [
      ...(materialDelivery || []).map((material) => material.count || 0),
      ...(hasFines ? [finesCount] : []),
    ],
  };
};
